import React, { useState, useRef, useEffect, useCallback } from 'react';

import { Drawer, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { withStyles } from '@mui/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import DownloadAppButton from 'components/DownloadMobileApp';
import ErrorBoundaries from 'components/ErrorBoundaries';
import RemoveScrollbar from 'components/RemoveScrollbar';
import ArrowDown from 'components/SvgComponents/icons/ArrowDown';
import reducer from 'containers/OneProjectPage/HomePage/reducer';
import saga from 'containers/OneProjectPage/HomePage/saga';
import { FONTSIZE_S } from 'theme/typography';
import globalMessages from 'translations/messages/global-messages';
import { oneProjectPages } from 'utils/constants';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';

const MIN_SIDEBAR_WIDTH = 180;
const MAX_SIDEBAR_WIDTH = 500;
const DEFAULT_SIDEBAR_WIDTH = 240;
const SIDEBAR_WIDTH_STORAGE_KEY = 'leftNavigationWidth';

export const styles = theme => ({
  drawer: {
    display: 'flex',
    zIndex: theme.zIndex.appBar,
    flexDirection: 'column',
  },
  drawerPaper: {
    height: '100%',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: '85px',
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  resizeHandle: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: '8px',
    cursor: 'col-resize',
    zIndex: theme.zIndex.appBar + 1,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.3,
    },
    '&:active': {
      backgroundColor: theme.palette.primary.main,
      opacity: 0.5,
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  button: {
    display: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
      alignSelf: 'flex-end',
    },
  },
  icon: {
    willChange: 'transform',
    transition: 'transform 250ms',
  },
  iconOpen: {
    transform: 'rotate(180deg)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    icon: {
      transition: 'transform 0ms',
    },
  },
  drawerPaperWrap: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    padding: '2em 1em 1rem 1em',
    borderLeft: `1px solid ${theme.palette.divider}`,
    gap: '1rem',
    [theme.breakpoints.down('xs')]: {
      padding: '1em',
      display: 'none',
    },
  },
  drawerPaperWrapOpen: {
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  downloadButton: {
    marginInline: 'auto',
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  settingsItem: {
    padding: '0.5rem 1em',
    color: theme.palette.text.primary,
    gap: '0.5rem',
  },
  settingsLabel: {
    fontSize: FONTSIZE_S,
  },
  activeLabel: {
    color: theme.palette.info.main,
  },
  wrapper: {
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '2.5rem',
  },
});

const LeftNavigation = ({ classes, children, menuOptions, match, intl }) => {
  const [expanded, setExpanded] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    return savedWidth ? parseInt(savedWidth, 10) : DEFAULT_SIDEBAR_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const { url } = match;
  const { menuList, component, name } = menuOptions;
  const sidebarRef = useRef(null);
  const sidebarWidthRef = useRef(sidebarWidth);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(
    () => {
      sidebarWidthRef.current = sidebarWidth;
    },
    [sidebarWidth],
  );

  const onToggleMenuClick = () => {
    setExpanded(prevState => !prevState);
  };

  const startResizing = useCallback(() => {
    setIsResizing(true);
  }, []);

  const stopResizing = useCallback(
    () => {
      setIsResizing(false);
      if (!isSmallScreen) {
        localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, sidebarWidthRef.current.toString());
      }
    },
    [isSmallScreen],
  );

  const resize = useCallback(
    mouseMoveEvent => {
      if (isResizing) {
        const newWidth = mouseMoveEvent.clientX;
        if (newWidth >= MIN_SIDEBAR_WIDTH && newWidth <= MAX_SIDEBAR_WIDTH) {
          setSidebarWidth(newWidth);
        }
      }
    },
    [isResizing],
  );

  useEffect(
    () => {
      window.addEventListener('mousemove', resize);
      window.addEventListener('mouseup', stopResizing);
      return () => {
        window.removeEventListener('mousemove', resize);
        window.removeEventListener('mouseup', stopResizing);
      };
    },
    [resize, stopResizing],
  );

  if (!component) {
    return null;
  }

  const COMPONENT = component;

  const renderButton = () => {
    if (
      url.includes(oneProjectPages.organizations) &&
      url.includes(oneProjectPages.forms) &&
      !match.params?.idData
    ) {
      return null;
    }
    return <DownloadAppButton className={classes.downloadButton} />;
  };

  const effectiveWidth = isSmallScreen ? 85 : sidebarWidth;

  return (
    <Drawer
      variant="permanent"
      anchor="left"
      classes={{ root: classes.drawer, paper: classes.drawerPaper }}
      ref={sidebarRef}
      sx={{ '& .MuiDrawer-paper': { width: effectiveWidth } }}
    >
      <ErrorBoundaries>
        <IconButton aria-expanded={expanded} onClick={onToggleMenuClick} className={classes.button}>
          <ArrowDown
            className={classNames(classes.icon, {
              [classes.iconOpen]: expanded,
            })}
            height={16}
            width={16}
          />
        </IconButton>
        <div
          className={classNames(classes.drawerPaperWrap, {
            [classes.drawerPaperWrapOpen]: expanded,
          })}
        >
          {children}
          <RemoveScrollbar>
            <div className={classes.wrapper}>
              <div>
                {React.isValidElement(COMPONENT) ? (
                  React.cloneElement(COMPONENT, { list: menuList || [], name })
                ) : (
                  <COMPONENT list={menuList || []} name={name} />
                )}
              </div>
            </div>
          </RemoveScrollbar>

          {renderButton()}
        </div>
        <div
          className={classes.resizeHandle}
          onMouseDown={startResizing}
          role="button"
          aria-label={intl.formatMessage(globalMessages.zoom_in)}
          tabIndex={0}
        />
      </ErrorBoundaries>
    </Drawer>
  );
};

LeftNavigation.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object,
  menuOptions: PropTypes.shape({
    menuList: PropTypes.arrayOf(PropTypes.object),
    component: PropTypes.oneOfType([PropTypes.node, () => null]),
    name: PropTypes.string,
  }),
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.array]),
  intl: PropTypes.object.isRequired,
};

const withSaga = injectSaga({ key: 'projectHomePage', saga });
const withReducer = injectReducer({ key: 'projectHomePage', reducer });

export default compose(
  withRouter,
  withStyles(styles),
  withSaga,
  withReducer,
  injectIntl,
)(LeftNavigation);
